import { Controller } from "@hotwired/stimulus"

// Panneaux restés ouverts à travers un remplacement de turbo-frame (#2379).
// La barre de réglages vit DANS le frame "market-list" que ses propres liens de
// filtre ciblent : chaque clic remplace le panneau par un markup neuf, donc
// fermé. Score du signal et Compétitions étant multi-sélection, il faut pouvoir
// enchaîner les clics — on rouvre à la reconnexion du contrôleur.
//
// Un Set de module, pas sessionStorage : l'état ne doit survivre ni à un
// rechargement, ni à un autre onglet, ni à une navigation Turbo Drive (d'où le
// vidage sur turbo:before-render, qui ne se déclenche QUE pour Drive, jamais
// pour un rendu de frame — c'est exactement la frontière voulue). Rejouer le
// rendu plutôt que figer le panneau (data-turbo-permanent) est ce qui permet
// aux compteurs de facette et aux cases cochées de refléter le nouvel état.
const openKeys = new Set()
document.addEventListener("turbo:before-render", () => openKeys.clear())

// Un seul popover ouvert à la fois : `toggle` stoppe la propagation du clic (pour
// ne pas déclencher sa propre fermeture au clic extérieur), donc le clic sur un
// second bouton n'atteignait jamais l'écouteur du premier et les deux panneaux se
// superposaient (Compétitions puis Filtres). Chaque ouverture le signale ici.
const OPEN_EVENT = "dropdown:open"

// Popover générique (#2379) : compétitions, panneau Filtres, menu Tri — même
// contrat que mobile_nav_controller (menu/button targets, aria-expanded,
// fermeture au clic extérieur), généralisé pour être réutilisé partout où un
// bouton ouvre un panneau flottant sur desktop.
export default class extends Controller {
  static targets = ["menu", "button"]
  // Identifiant stable du panneau — sans lui, aucune persistance (comportement
  // d'origine pour tout appelant hors barre de réglages).
  static values = { key: String, name: String }

  connect() {
    this._close = (e) => { if (!this.element.contains(e.target)) this.close() }
    this._escape = (e) => { if (e.key === "Escape") this.close() }
    document.addEventListener("click", this._close)
    document.addEventListener("keydown", this._escape)
    // Ouverture à distance (#2403) : la ligne compacte émet `filters:open` avec
    // le `name` du panneau à ouvrir, plutôt que de dupliquer le panneau.
    this._remoteOpen = (e) => { if (this.hasNameValue && e.detail?.name === this.nameValue) this._open() }
    document.addEventListener("filters:open", this._remoteOpen)
    this._closeWhenOtherOpens = (e) => { if (e.detail?.element !== this.element) this.close() }
    document.addEventListener(OPEN_EVENT, this._closeWhenOtherOpens)
    if (this._remembered) this._open()
  }

  disconnect() {
    document.removeEventListener("click", this._close)
    document.removeEventListener("keydown", this._escape)
    document.removeEventListener("filters:open", this._remoteOpen)
    document.removeEventListener(OPEN_EVENT, this._closeWhenOtherOpens)
  }

  toggle(event) {
    event.stopPropagation()
    if (this.menuTarget.classList.contains("hidden")) this._open()
    else this.close()
  }

  close() {
    this.menuTarget.classList.add("hidden")
    this._setExpanded(false)
    if (this.hasKeyValue) openKeys.delete(this.keyValue)
  }

  get _remembered() {
    return this.hasKeyValue && openKeys.has(this.keyValue)
  }

  _open() {
    document.dispatchEvent(new CustomEvent(OPEN_EVENT, { detail: { element: this.element } }))
    this.menuTarget.classList.remove("hidden")
    this._setExpanded(true)
    if (this.hasKeyValue) openKeys.add(this.keyValue)
  }

  _setExpanded(expanded) {
    if (this.hasButtonTarget) this.buttonTarget.setAttribute("aria-expanded", String(expanded))
  }
}
